import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

export default function LocationSection() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="contato" className="relative py-24 md:py-32 lg:py-40 bg-dark-900 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div ref={ref} className="text-center mb-16 md:mb-20">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-premium-400 text-xs tracking-[0.3em] uppercase font-medium"
          >
            Localização
          </motion.span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mt-4">
            Venha nos{' '}
            <span className="text-gradient-gold">conhecer</span>
          </h2>
          <motion.div
            initial={{ width: 0 }}
            animate={isInView ? { width: '80px' } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="h-[1px] bg-premium-500 mx-auto mt-6"
          ></motion.div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="w-full lg:w-2/3"
          >
            <div className="rounded-lg overflow-hidden border border-white/5 h-[350px] md:h-[450px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15925.123456789!2d-38.5267!3d-3.7319!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zM8KwNDMnNTQuOCJTIDM4wrAzMSczNi4xIlc!5e0!3m2!1spt-BR!2sbr!4v1690000000000!5m2!1spt-BR!2sbr"
                width="100%"
                height="100%"
                style={{ border: 0, filter: 'grayscale(1) invert(0.92)' }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Localização da Tal Pai, Tal Filho Barbearia"
              ></iframe>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="w-full lg:w-1/3"
          >
            <div className="bg-dark-800 rounded-lg p-6 md:p-8 border border-white/5">
              <h3 className="font-heading text-xl md:text-2xl font-semibold text-white mb-6">
                Informações
              </h3>

              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-premium-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-map-pin-line text-premium-400"></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Endereço</p>
                    <p className="text-white/80 text-sm">
                      Av. Senador Virgílio Távora, 800<br />
                      Aldeota, Fortaleza - CE
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-premium-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-time-line text-premium-400"></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Horários</p>
                    <p className="text-white/80 text-sm">
                      Seg - Sex: 9h às 20h<br />
                      Sábado: 8h às 18h<br />
                      Domingo: Fechado
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-md bg-premium-600/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-phone-line text-premium-400"></i>
                  </div>
                  <div>
                    <p className="text-white/50 text-xs uppercase tracking-wider mb-1">Telefone</p>
                    <p className="text-white/80 text-sm">(85) 99999-9999</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-3 mt-8 pt-6 border-t border-white/5">
                <a
                  href="https://maps.google.com/?q=Av.+Senador+Virgílio+Távora+800+Aldeota+Fortaleza+CE"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-dark-700 hover:bg-dark-600 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-map-pin-2-line"></i>
                  Abrir no Google Maps
                </a>
                <a
                  href="https://wa.me/5585999999999?text=Ol%C3%A1!%20Gostaria%20de%20agendar%20um%20hor%C3%A1rio."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-md bg-green-700 hover:bg-green-600 text-white text-sm font-medium tracking-wide transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line"></i>
                  Agendar pelo WhatsApp
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}